import { lookupMembershipByPlate } from '@jq4race/api-client';
import type { MembershipLookupResponse } from '@jq4race/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

// frequent-customer-memberships · Slice S4 (design §7, Capability 7) — fires
// the advisory GET /valet/membership/lookup call so the check-in form can show
// the "MEMBER — do not charge" banner as the valet types the plate. ADVISORY
// ONLY: the server-authoritative coverage decision is enforced independently
// inside CreateTicketUseCase at submit time, and the confirmation screen shows
// the authoritative result from the create response — this hook only drives the
// pre-submit hint.
//
// The lookup is triggered two ways (both call the SAME underlying request):
//   - `lookup(plate)`        — immediate, used on blur (belt-and-suspenders).
//   - `lookupDebounced(plate)` — debounced, used on every keystroke so the
//     banner appears while typing even if the field never loses focus (mobile
//     keyboards frequently don't emit a reliable blur before submit).

const DEFAULT_DEBOUNCE_MS = 400;

interface UseMembershipLookupResult {
  result: MembershipLookupResponse | null;
  loading: boolean;
  lookup: (plate: string) => Promise<void>;
  lookupDebounced: (plate: string) => void;
  reset: () => void;
}

export function useMembershipLookup(debounceMs = DEFAULT_DEBOUNCE_MS): UseMembershipLookupResult {
  const [result, setResult] = useState<MembershipLookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const lookup = useCallback(async (plate: string) => {
    const trimmed = plate.trim();
    if (!trimmed) {
      setResult(null);
      return;
    }

    setLoading(true);
    try {
      const response = await lookupMembershipByPlate(trimmed);
      setResult(response);
    } catch {
      // Best-effort — a failed lookup must never block check-in (advisory only).
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const lookupDebounced = useCallback(
    (plate: string) => {
      clearTimer();
      // Clear any stale banner IMMEDIATELY on every edit — a banner from a
      // previously-resolved plate must never survive into an edited plate.
      setResult(null);
      const trimmed = plate.trim();
      if (!trimmed) return;
      timerRef.current = setTimeout(() => {
        void lookup(trimmed);
      }, debounceMs);
    },
    [clearTimer, lookup, debounceMs],
  );

  const reset = useCallback(() => {
    clearTimer();
    setResult(null);
  }, [clearTimer]);

  // Cancel any pending debounced lookup on unmount.
  useEffect(() => clearTimer, [clearTimer]);

  return { result, loading, lookup, lookupDebounced, reset };
}
